import { useEffect, useState } from "react";
import {
    Link2,
    CheckCircle2,
    Copy,
    Unplug,
    RefreshCw,
    Clock,
    KeyRound,
    Server,
    AlertTriangle,
    ShieldCheck,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import {
    ERP_CONNECTION_STATUS,
    ERP_CONNECTION_GENERATE,
    ERP_CONNECTION_REVOKE,
} from "../../constants/api";

const ConnectERP = () => {
    const { fetch, create } = useApiMethods();
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [revoking, setRevoking] = useState(false);
    const [connection, setConnection] = useState(null);
    const [token, setToken] = useState(null);

    const loadStatus = async () => {
        const res = await fetch(ERP_CONNECTION_STATUS, {}, setLoading);
        if (res?.status === "success") {
            setConnection(res.data);
        }
    };

    useEffect(() => {
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerate = async () => {
        const res = await create(ERP_CONNECTION_GENERATE, {}, false, setGenerating);
        if (res?.status === "success") {
            setToken(res.data.token);
            ToastMsg("success", "Connection token generated");
            loadStatus();
        } else {
            ToastMsg("error", res?.message || "Failed to generate token");
        }
    };

    const handleRevoke = async () => {
        if (!window.confirm("Revoke the ERP connection? The ERP will lose access immediately.")) return;
        const res = await create(ERP_CONNECTION_REVOKE, {}, false, setRevoking);
        if (res?.status === "success") {
            ToastMsg("success", "Connection revoked");
            setConnection(null);
            setToken(null);
        } else {
            ToastMsg("error", res?.message || "Failed to revoke");
        }
    };

    const handleCopy = () => {
        if (!token) return;
        navigator.clipboard.writeText(token);
        ToastMsg("success", "Token copied to clipboard");
    };

    // ERP connection endpoints are admin-only on the backend (the `erp` module
    // requires is_admin). Hide the screen entirely for non-admins.
    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
                <ShieldCheck className="w-12 h-12 text-amber-500" />
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                    You don't have permission to manage ERP integration.
                </p>
            </div>
        );
    }

    const isConnected = !!(connection && connection.is_active);

    return (
        <div className="max-w-3xl space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Link2 size={22} />
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            ERP Integration
                        </h2>
                        <span
                            className={cn(
                                "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border",
                                isConnected
                                    ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
                                    : "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"
                            )}
                        >
                            <span className={cn("w-1.5 h-1.5 rounded-full", isConnected ? "bg-green-500" : "bg-gray-400")} />
                            {isConnected ? "Connected" : "Not connected"}
                        </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Generate a connection token to link this HRMS with the ERP platform. The ERP
                        uses this token to securely read employee, attendance, and payroll data.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
                    <div className="animate-pulse space-y-3">
                        <div className="h-10 w-2/3 bg-gray-100 dark:bg-gray-700 rounded" />
                        <div className="h-16 w-full bg-gray-100 dark:bg-gray-700 rounded" />
                    </div>
                </div>
            ) : (
                <>
                    {/* Connected state */}
                    {isConnected && (
                        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-5 space-y-4">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 flex-shrink-0 bg-green-100 dark:bg-green-900/40 rounded-xl flex items-center justify-center">
                                        <CheckCircle2 size={20} className="text-green-600 dark:text-green-400" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                            {connection.org_name || "ERP Connection"}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Connection active</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
                                <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
                                    <div className="text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide">
                                        <KeyRound size={12} /> API Key
                                    </div>
                                    <div className="font-mono text-sm text-gray-900 dark:text-gray-100 truncate">
                                        {connection.api_key || "—"}
                                    </div>
                                </div>
                                <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
                                    <div className="text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide">
                                        <Clock size={12} /> Last Connected
                                    </div>
                                    <div className="text-sm text-gray-900 dark:text-gray-100">
                                        {connection.last_connected_at
                                            ? new Date(connection.last_connected_at).toLocaleString()
                                            : "Never"}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2 pt-1">
                                <Button
                                    onClick={handleGenerate}
                                    disabled={generating}
                                    variant="brand"
                                >
                                    <RefreshCw size={14} className={cn("mr-2", generating && "animate-spin")} />
                                    {generating ? "Generating..." : "Regenerate Token"}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleRevoke}
                                    disabled={revoking}
                                    className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                                >
                                    <Unplug size={14} className="mr-2" />
                                    {revoking ? "Revoking..." : "Revoke"}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* New token display */}
                    {token && (
                        <div className="rounded-2xl border border-sidebar/30 bg-sidebar/5 dark:bg-sidebar-primary/10 shadow-sm p-5 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <KeyRound size={16} className="text-sidebar-primary" />
                                    Connection Token
                                </h3>
                                <Button
                                    onClick={handleCopy}
                                    size="sm"
                                    variant="brand"
                                >
                                    <Copy size={14} className="mr-1.5" />
                                    Copy
                                </Button>
                            </div>
                            <textarea
                                readOnly
                                value={token}
                                rows={5}
                                onFocus={(e) => e.target.select()}
                                className="w-full px-3 py-2 font-mono text-xs rounded-xl border border-sidebar/30 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-sidebar-primary resize-none"
                            />
                            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
                                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                                <span>
                                    Copy this token and paste it in the ERP's <strong>Connect HRMS</strong> settings page.
                                    It's shown only once — regenerate if lost.
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Disconnected state */}
                    {!isConnected && !token && (
                        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-5 space-y-5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gray-100 dark:bg-gray-900/40 grid place-items-center">
                                    <Server size={18} className="text-gray-400" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">No ERP connected yet</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Generate a token to start the connection.
                                    </p>
                                </div>
                            </div>

                            <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-4">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 text-sm">
                                    How to connect
                                </h4>
                                <ol className="space-y-2.5">
                                    {[
                                        <>Click <strong>Generate Token</strong> below</>,
                                        <>Copy the token that appears</>,
                                        <>In the ERP, go to <strong>Settings → Connect HRMS</strong></>,
                                        <>Paste the token and click <strong>Connect</strong></>,
                                    ].map((step, i) => (
                                        <li key={i} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                                            <span className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-sidebar/10 text-sidebar-primary text-xs font-semibold grid place-items-center">
                                                {i + 1}
                                            </span>
                                            <span>{step}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>

                            <Button
                                onClick={handleGenerate}
                                disabled={generating}
                                variant="brand"
                            >
                                <Link2 size={14} className="mr-2" />
                                {generating ? "Generating..." : "Generate Token"}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ConnectERP;
